import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import type { ForecastAccount } from '../types';
import { MONTHS } from '../types';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CellCoord {
  rowIndex: number;   // Index into allAccounts
  colIndex: number;   // 0-11 month index
}

export interface CellRange {
  startRow: number;
  endRow: number;
  startCol: number;
  endCol: number;
}

interface UseGridSelectionOpts {
  totalRows: number;
  totalCols: number;         // always 12
  isMonthEditable: (colIndex: number) => boolean;
  onCellSave?: (chartOfAccountId: number, month: number, value: number) => Promise<void>;
  onBulkPaste?: (entries: { chartOfAccountId: number; month: number; value: number }[]) => Promise<void>;
  allAccounts: ForecastAccount[];
  cellRefs: React.MutableRefObject<Map<string, HTMLTableCellElement>>;
  isReadOnly: boolean;
  /** Scroll container that owns the grid — used for scroll-into-view and drag auto-scroll */
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
}

// ─── Scroll helpers ──────────────────────────────────────────────────────────

/** Width of the sticky left column(s) and height of the sticky header inside the scroll container. */
function getStickyInsets(container: HTMLElement): { left: number; top: number } {
  let left = 0;
  let top = 0;
  const headRow = container.querySelector('thead tr');
  if (headRow) {
    headRow.querySelectorAll<HTMLElement>('th').forEach(th => {
      const pos = getComputedStyle(th).position;
      if (pos === 'sticky' && getComputedStyle(th).left === '0px') left += th.getBoundingClientRect().width;
    });
  }
  const thead = container.querySelector('thead');
  if (thead) top = thead.getBoundingClientRect().height;
  return { left, top };
}

/**
 * Scroll `cell` fully into view inside `container`, accounting for the sticky
 * account column on the left and the sticky header on top. Plain `focus()`
 * scrolls the element into the box but happily leaves it hidden under the
 * sticky column, which reads as "nothing happened".
 */
export function scrollCellIntoView(cell: HTMLElement, container: HTMLElement | null | undefined) {
  if (!container) return;
  const c = container.getBoundingClientRect();
  const r = cell.getBoundingClientRect();
  const { left: stickyLeft, top: stickyTop } = getStickyInsets(container);
  const pad = 8;
  let dx = 0;
  let dy = 0;
  if (r.left < c.left + stickyLeft + pad) dx = r.left - (c.left + stickyLeft + pad);
  else if (r.right > c.right - pad) dx = r.right - (c.right - pad);
  if (r.top < c.top + stickyTop + pad) dy = r.top - (c.top + stickyTop + pad);
  else if (r.bottom > c.bottom - pad) dy = r.bottom - (c.bottom - pad);
  if (dx || dy) container.scrollBy({ left: dx, top: dy, behavior: 'auto' });
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useGridSelection({
  totalRows,
  totalCols,
  isMonthEditable,
  onCellSave,
  onBulkPaste,
  allAccounts,
  cellRefs,
  isReadOnly,
  scrollContainerRef,
}: UseGridSelectionOpts) {
  const [anchor, setAnchor] = useState<CellCoord | null>(null);
  const [current, setCurrent] = useState<CellCoord | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [copiedValue, setCopiedValue] = useState<number | null>(null);
  const [copiedRange, setCopiedRange] = useState<{ values: number[][]; range: CellRange } | null>(null);
  const [copiedCellRange, setCopiedCellRange] = useState<CellRange | null>(null);

  // Track dragging state in a ref so window listeners can read latest
  const isDraggingRef = useRef(false);

  // ─── Computed selection range ──────────────────────────────────────────────

  const selectedRange: CellRange | null = useMemo(() => {
    if (!anchor) return null;
    if (!current) {
      return { startRow: anchor.rowIndex, endRow: anchor.rowIndex, startCol: anchor.colIndex, endCol: anchor.colIndex };
    }
    return {
      startRow: Math.min(anchor.rowIndex, current.rowIndex),
      endRow: Math.max(anchor.rowIndex, current.rowIndex),
      startCol: Math.min(anchor.colIndex, current.colIndex),
      endCol: Math.max(anchor.colIndex, current.colIndex),
    };
  }, [anchor, current]);

  const isCellSelected = useCallback((rowIndex: number, colIndex: number): boolean => {
    if (!selectedRange) return false;
    return (
      rowIndex >= selectedRange.startRow && rowIndex <= selectedRange.endRow &&
      colIndex >= selectedRange.startCol && colIndex <= selectedRange.endCol
    );
  }, [selectedRange]);

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const getValue = useCallback((r: number, c: number): number => {
    const account = allAccounts[r];
    if (!account) return 0;
    const monthKey = MONTHS[c];
    return account.monthlyValues[monthKey] || 0;
  }, [allAccounts]);

  const focusCell = useCallback((row: number, col: number) => {
    const key = `${row}-${col}`;
    const cell = cellRefs.current.get(key);
    if (cell) {
      cell.focus({ preventScroll: true });
      scrollCellIntoView(cell, scrollContainerRef?.current);
    }
  }, [cellRefs, scrollContainerRef]);

  const clampRow = (r: number) => Math.max(0, Math.min(totalRows - 1, r));
  const clampCol = (c: number) => Math.max(0, Math.min(totalCols - 1, c));

  /** Excel-like jump: find edge of contiguous data in a direction */
  const findEdge = useCallback((rowIndex: number, colIndex: number, direction: 'up' | 'down' | 'left' | 'right'): CellCoord => {
    // Every cell in this grid holds a value — a zero is a forecast of zero, not
    // an empty cell — so Excel's "edge of the data" is the edge of the grid.
    // Stopping at the first zero left Ctrl+→ parked on the working month.
    switch (direction) {
      case 'up': return { rowIndex: 0, colIndex };
      case 'down': return { rowIndex: Math.max(0, totalRows - 1), colIndex };
      case 'left': return { rowIndex, colIndex: 0 };
      default: return { rowIndex, colIndex: Math.max(0, totalCols - 1) };
    }
  }, [totalRows, totalCols]);

  // ─── Selection helpers ─────────────────────────────────────────────────────

  /** Clear selection highlight only (preserve clipboard and marching ants) */
  const clearSelectionHighlight = useCallback(() => {
    setAnchor(null);
    setCurrent(null);
    setIsDragging(false);
    isDraggingRef.current = false;
  }, []);

  /** Clear everything: selection highlight + clipboard + marching ants */
  const clearSelection = useCallback(() => {
    clearSelectionHighlight();
    setCopiedCellRange(null);
    setCopiedValue(null);
    setCopiedRange(null);
  }, [clearSelectionHighlight]);

  /** Build inline boxShadow for selection border edges */
  const getSelectionStyle = useCallback((rowIndex: number, colIndex: number): React.CSSProperties => {
    if (!selectedRange) return {};
    if (
      rowIndex < selectedRange.startRow || rowIndex > selectedRange.endRow ||
      colIndex < selectedRange.startCol || colIndex > selectedRange.endCol
    ) return {};

    // If cell is in the copied range, let marching ants handle the border — skip solid box-shadow
    const isInCopiedRange = copiedCellRange &&
      rowIndex >= copiedCellRange.startRow && rowIndex <= copiedCellRange.endRow &&
      colIndex >= copiedCellRange.startCol && colIndex <= copiedCellRange.endCol;

    if (isInCopiedRange) {
      return {
        background: 'color-mix(in srgb, var(--lodge-orange-100) 60%, transparent)',
      };
    }

    const shadows: string[] = [];
    if (rowIndex === selectedRange.startRow) shadows.push('inset 0 2px 0 0 var(--lodge-orange-500)');
    if (rowIndex === selectedRange.endRow) shadows.push('inset 0 -2px 0 0 var(--lodge-orange-500)');
    if (colIndex === selectedRange.startCol) shadows.push('inset 2px 0 0 0 var(--lodge-orange-500)');
    if (colIndex === selectedRange.endCol) shadows.push('inset -2px 0 0 0 var(--lodge-orange-500)');

    return {
      background: 'color-mix(in srgb, var(--lodge-orange-100) 60%, transparent)',
      boxShadow: shadows.length > 0 ? shadows.join(', ') : 'inset 0 0 0 1px var(--lodge-orange-200)',
    };
  }, [selectedRange, copiedCellRange]);

  // ─── Copy ──────────────────────────────────────────────────────────────────

  const handleCopy = useCallback(() => {
    if (!selectedRange) return;

    const { startRow, endRow, startCol, endCol } = selectedRange;
    const isSingle = startRow === endRow && startCol === endCol;

    // Store copied range for marching ants indicator
    setCopiedCellRange({ ...selectedRange });

    if (isSingle) {
      const val = getValue(startRow, startCol);
      setCopiedValue(val);
      setCopiedRange(null);
      navigator.clipboard.writeText(val.toString()).catch(() => {});
      toast.success('Copied');
    } else {
      const values: number[][] = [];
      const rows: string[] = [];
      for (let r = startRow; r <= endRow; r++) {
        const rowVals: number[] = [];
        const rowStrs: string[] = [];
        for (let c = startCol; c <= endCol; c++) {
          const v = getValue(r, c);
          rowVals.push(v);
          rowStrs.push(v.toString());
        }
        values.push(rowVals);
        rows.push(rowStrs.join('\t'));
      }
      setCopiedRange({ values, range: selectedRange });
      setCopiedValue(null);
      navigator.clipboard.writeText(rows.join('\n')).catch(() => {});
      const cellCount = (endRow - startRow + 1) * (endCol - startCol + 1);
      toast.success(`Copied ${cellCount} cells`);
    }
  }, [selectedRange, getValue]);

  /** Returns which edges of the marching ants border to show for a cell, or null if not in copied range */
  const getCopyIndicatorEdges = useCallback((rowIndex: number, colIndex: number): { top: boolean; bottom: boolean; left: boolean; right: boolean } | null => {
    if (!copiedCellRange) return null;
    const { startRow, endRow, startCol, endCol } = copiedCellRange;
    if (rowIndex < startRow || rowIndex > endRow || colIndex < startCol || colIndex > endCol) return null;
    return {
      top: rowIndex === startRow,
      bottom: rowIndex === endRow,
      left: colIndex === startCol,
      right: colIndex === endCol,
    };
  }, [copiedCellRange]);

  // ─── Paste ─────────────────────────────────────────────────────────────────

  const handlePasteToRange = useCallback((targetRange: CellRange) => {
    if ((!onCellSave && !onBulkPaste) || isReadOnly) return;

    const { startRow, endRow, startCol, endCol } = targetRange;

    // Use internal clipboard first (synchronous, instant).
    // Only try system clipboard if we have NO internal data — and do it async without blocking.
    const sourceValues = copiedRange?.values || null;
    const singleVal = !sourceValues ? copiedValue : null;

    if (!sourceValues && singleVal === null) {
      // No internal clipboard — try system clipboard asynchronously
      navigator.clipboard.readText().then(text => {
        if (text && (text.includes('\t') || text.includes('\n'))) {
          const parsed = text.trim().split('\n').map(row =>
            row.split('\t').map(v => {
              const cleaned = v.replace(/[$,\s]/g, '');
              const n = parseFloat(cleaned);
              return isNaN(n) ? 0 : n;
            })
          );
          if (parsed.length > 0 && parsed[0].length > 0) {
            executePaste(targetRange, parsed, null);
            return;
          }
        }
        // Try single number from system clipboard
        if (text) {
          const cleaned = text.replace(/[$,\s]/g, '');
          const n = parseFloat(cleaned);
          if (!isNaN(n)) {
            executePaste(targetRange, null, n);
            return;
          }
        }
        toast.error('Nothing to paste');
      }).catch(() => {
        toast.error('Nothing to paste');
      });
      return;
    }

    // Fast path — internal clipboard, fully synchronous
    executePaste(targetRange, sourceValues, singleVal);
  }, [onCellSave, onBulkPaste, isReadOnly, copiedValue, copiedRange, isMonthEditable, allAccounts]);

  /** Synchronous paste execution — builds entries and fires save immediately */
  const executePaste = useCallback((targetRange: CellRange, sourceValues: number[][] | null, singleVal: number | null) => {
    const { startRow, endRow, startCol, endCol } = targetRange;

    const entries: { chartOfAccountId: number; month: number; value: number }[] = [];
    let skippedCount = 0;
    let totalCount = 0;

    for (let r = startRow; r <= endRow; r++) {
      for (let c = startCol; c <= endCol; c++) {
        totalCount++;
        if (!isMonthEditable(c)) { skippedCount++; continue; }
        const account = allAccounts[r];
        if (!account) continue;

        let value: number;
        if (sourceValues) {
          const srcRows = sourceValues.length;
          const srcCols = sourceValues[0]?.length || 0;
          const srcR = (r - startRow) % srcRows;
          const srcC = (c - startCol) % srcCols;
          value = sourceValues[srcR][srcC];
        } else {
          value = singleVal!;
        }

        entries.push({ chartOfAccountId: account.chartOfAccountId, month: c + 1, value });
      }
    }

    if (entries.length === 0) {
      toast.error('No editable cells in selection');
      return;
    }

    // Clear marching ants after paste
    setCopiedCellRange(null);

    // Toast immediately
    if (skippedCount > 0) {
      toast.success(`Pasted to ${entries.length} of ${totalCount} cells (${skippedCount} locked)`);
    } else {
      toast.success(`Pasted to ${entries.length} cell${entries.length !== 1 ? 's' : ''}`);
    }

    // Fire-and-forget: bulk paste (single optimistic cache update) or individual saves
    if (onBulkPaste) {
      onBulkPaste(entries).catch(() => {
        toast.error('Failed to save some overrides');
      });
    } else if (onCellSave) {
      for (const entry of entries) {
        onCellSave(entry.chartOfAccountId, entry.month, entry.value).catch(() => {
          toast.error('Failed to save override');
        });
      }
    }
  }, [onCellSave, onBulkPaste, isMonthEditable, allAccounts]);

  // ─── Mouse handlers ────────────────────────────────────────────────────────

  const handleCellMouseDown = useCallback((rowIndex: number, colIndex: number, e: React.MouseEvent) => {
    if (e.button !== 0) return; // Left-click only

    if (e.shiftKey && anchor) {
      // Extend selection from anchor to this cell
      e.preventDefault();
      setCurrent({ rowIndex, colIndex });
    } else {
      // Start new selection
      setAnchor({ rowIndex, colIndex });
      setCurrent(null);
      setIsDragging(true);
      isDraggingRef.current = true;
    }
  }, [anchor]);

  const handleCellMouseEnter = useCallback((rowIndex: number, colIndex: number) => {
    if (isDraggingRef.current) {
      setCurrent({ rowIndex, colIndex });
    }
  }, []);

  // Window-level mouseup to end drag
  useEffect(() => {
    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        setIsDragging(false);
        isDraggingRef.current = false;
      }
    };

    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  // Drag auto-scroll: when the pointer leaves the scroll container mid-drag,
  // keep scrolling toward it and extend the selection to whatever cell ends up
  // under the pointer. Without this, `mouseenter` never fires for off-screen
  // columns and a Jan→Dec drag stops at the last visible month.
  useEffect(() => {
    if (!isDragging) return;
    const container = scrollContainerRef?.current;
    if (!container) return;

    let pointer: { x: number; y: number } | null = null;
    let raf = 0;
    const EDGE = 24;      // px inside the edge that starts scrolling
    const MAX_STEP = 28;  // px per frame at full speed

    const resolveCellUnderPointer = () => {
      if (!pointer) return;
      const c = container.getBoundingClientRect();
      const { left: stickyLeft, top: stickyTop } = getStickyInsets(container);
      // Probe inside the visible data area (right of the sticky column, below the header)
      const x = Math.min(Math.max(pointer.x, c.left + stickyLeft + 2), c.right - 2);
      const y = Math.min(Math.max(pointer.y, c.top + stickyTop + 2), c.bottom - 2);
      const el = document.elementFromPoint(x, y) as HTMLElement | null;
      const td = el?.closest<HTMLElement>('td[data-grid-row]');
      if (!td) return;
      const rowIndex = Number(td.dataset.gridRow);
      const colIndex = Number(td.dataset.gridCol);
      if (Number.isNaN(rowIndex) || Number.isNaN(colIndex)) return;
      setCurrent(prev => (prev && prev.rowIndex === rowIndex && prev.colIndex === colIndex) ? prev : { rowIndex, colIndex });
    };

    const tick = () => {
      raf = 0;
      if (!isDraggingRef.current || !pointer) return;
      const c = container.getBoundingClientRect();
      const { left: stickyLeft, top: stickyTop } = getStickyInsets(container);
      const speed = (overshoot: number) => Math.sign(overshoot) * Math.min(MAX_STEP, Math.ceil(Math.abs(overshoot) / 4) + 4);
      let dx = 0;
      let dy = 0;
      const leftEdge = c.left + stickyLeft + EDGE;
      const rightEdge = c.right - EDGE;
      const topEdge = c.top + stickyTop + EDGE;
      const bottomEdge = c.bottom - EDGE;
      if (pointer.x < leftEdge) dx = speed(pointer.x - leftEdge);
      else if (pointer.x > rightEdge) dx = speed(pointer.x - rightEdge);
      if (pointer.y < topEdge) dy = speed(pointer.y - topEdge);
      else if (pointer.y > bottomEdge) dy = speed(pointer.y - bottomEdge);
      if (dx || dy) {
        container.scrollBy({ left: dx, top: dy, behavior: 'auto' });
        resolveCellUnderPointer();
        raf = requestAnimationFrame(tick);
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      pointer = { x: e.clientX, y: e.clientY };
      resolveCellUnderPointer();
      if (!raf) raf = requestAnimationFrame(tick);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isDragging, scrollContainerRef]);

  // ─── Keyboard handler ─────────────────────────────────────────────────────

  const handleKeyDown = useCallback((e: React.KeyboardEvent, rowIndex: number, colIndex: number) => {
    const isMod = e.ctrlKey || e.metaKey;

    // Ctrl+C / Cmd+C — Copy (use focused cell as fallback if no range selected)
    if (isMod && e.key === 'c') {
      e.preventDefault();
      if (!selectedRange) {
        // Set anchor to focused cell so handleCopy has a range to work with
        setAnchor({ rowIndex, colIndex });
        setCurrent(null);
      }
      // Need to defer copy to next tick so state updates are applied
      // Instead, just inline the single-cell copy for the no-selection case
      if (!selectedRange) {
        const val = getValue(rowIndex, colIndex);
        setCopiedValue(val);
        setCopiedRange(null);
        setCopiedCellRange({ startRow: rowIndex, endRow: rowIndex, startCol: colIndex, endCol: colIndex });
        navigator.clipboard.writeText(val.toString()).catch(() => {});
        toast.success('Copied');
      } else {
        handleCopy();
      }
      return;
    }

    // Ctrl+V / Cmd+V — Paste (use focused cell as target if no range selected)
    if (isMod && e.key === 'v') {
      e.preventDefault();
      if (!selectedRange) {
        // Temporarily set anchor to focused cell for paste target
        setAnchor({ rowIndex, colIndex });
        setCurrent(null);
      }
      // Inline paste for the no-selection case using focused cell
      const pasteRange = selectedRange || { startRow: rowIndex, endRow: rowIndex, startCol: colIndex, endCol: colIndex };
      handlePasteToRange(pasteRange);
      return;
    }

    // Escape — Clear selection and copy indicator
    if (e.key === 'Escape') {
      if (selectedRange || copiedCellRange) {
        e.preventDefault();
        clearSelection();
      }
      return;
    }

    // Arrow key handling with Shift and/or Ctrl modifiers
    const arrowDir = ({ ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' } as const)[e.key];
    if (!arrowDir) return;

    if (isMod && e.shiftKey) {
      // Ctrl+Shift+Arrow — Extend selection to edge
      e.preventDefault();
      const effectiveAnchor = anchor || { rowIndex, colIndex };
      if (!anchor) setAnchor(effectiveAnchor);
      const edge = findEdge(current?.rowIndex ?? rowIndex, current?.colIndex ?? colIndex, arrowDir);
      setCurrent(edge);
      return;
    }

    if (isMod && !e.shiftKey) {
      // Ctrl+Arrow — Jump to edge, clear selection highlight (keep clipboard)
      e.preventDefault();
      const edge = findEdge(rowIndex, colIndex, arrowDir);
      clearSelectionHighlight();
      setAnchor(edge);
      focusCell(edge.rowIndex, edge.colIndex);
      return;
    }

    if (e.shiftKey && !isMod) {
      // Shift+Arrow — Extend selection by 1
      e.preventDefault();
      const effectiveAnchor = anchor || { rowIndex, colIndex };
      if (!anchor) setAnchor(effectiveAnchor);

      const cur = current || effectiveAnchor;
      const dr = arrowDir === 'up' ? -1 : arrowDir === 'down' ? 1 : 0;
      const dc = arrowDir === 'left' ? -1 : arrowDir === 'right' ? 1 : 0;
      const newRow = clampRow(cur.rowIndex + dr);
      const newCol = clampCol(cur.colIndex + dc);
      setCurrent({ rowIndex: newRow, colIndex: newCol });
      return;
    }

    // Plain arrow with no modifiers — handled by EditableCell's existing navigation
    // Clear selection highlight when user navigates without Shift (keep clipboard for paste)
    if (selectedRange && !e.shiftKey && !isMod) {
      clearSelectionHighlight();
    }
  }, [anchor, current, selectedRange, copiedCellRange, findEdge, handleCopy, handlePasteToRange, clearSelection, clearSelectionHighlight, focusCell, getValue, totalRows, totalCols]);

  return {
    selectedRange,
    isCellSelected,
    getSelectionStyle,
    getCopyIndicatorEdges,
    copiedCellRange,
    handleCellMouseDown,
    handleCellMouseEnter,
    handleKeyDown,
    clearSelection,
    copiedValue,
    copiedRange,
    isDragging,
  };
}
